import React, { MouseEvent, useMemo } from "react";
import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { InteractiveElement, media, truncateText } from "@iventis/styles";
import { Comment as CommentType } from "@iventis/domain-model/model/comment";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { timeAgo } from "@iventis/utilities";
import { SimpleTooltip, UserThumbnail } from "@iventis/components";
import { useQuery } from "@tanstack/react-query";
import { StyledCommentWrapper } from "./comment-input";
import { useCommentsService } from "./comments-service";
import { CommentImageUpload, StyledCommentIcon } from "./comment-image-upload";
import { parseCommentTextContent } from "./comment-text-content-helpers";
import { CommentImageComponent } from "./comment-image";
import { CommentImage } from "./comment-types";

export type CommentProps = Pick<CommentType, "textContent" | "createdAt" | "id" | "lastUpdatedAt"> & {
    handleEditClick?: () => void;
    handleDeleteClick: () => void;
    handleViewAll?: () => void;
    handleImageUpload?: (image: CommentImage) => void;
    inCommentList?: boolean;
    className?: string;
    userId: string;
    dataTestId?: string;
    disableImageButton: boolean;
};

const User = ({ name, profileImg, className }: { name: string; profileImg: string; className?: string }) => (
    <div className={className}>
        <StyledUserThumbnail className="icon" imageUrl={profileImg} />
        <p className={truncateText}>{name}</p>
    </div>
);

interface CommentActionsProps {
    handleEditClick?: () => void;
    handleDeleteClick?: () => void;
    handleImageUpload?: (image: CommentImage) => void;
    disableImageButton: boolean;
    inCommentList?: boolean;
}

const CommentActions = ({ handleEditClick, handleDeleteClick, handleImageUpload, inCommentList, disableImageButton }: CommentActionsProps) => {
    const translate = useIventisTranslate();

    const onDeleteClicked = (event: MouseEvent) => {
        handleDeleteClick();
        event.stopPropagation();
    };

    const onEditClicked = (event: MouseEvent) => {
        handleEditClick();
        event.stopPropagation();
    };
    return (
        <StyledCommentActionWrapper>
            {handleEditClick && (
                <SimpleTooltip text={translate(Content.map8.comments.edit)} placement="bottom">
                    <StyledInteractiveElement data-testid="edit-comment-button" onClick={onEditClicked}>
                        <StyledCommentIcon icon={["far", "pen-to-square"]} size="lg" />
                    </StyledInteractiveElement>
                </SimpleTooltip>
            )}
            {!inCommentList && <CommentImageUpload disableImageIcon={disableImageButton} onFileUpload={handleImageUpload} replaceImageText={false} />}
            {handleDeleteClick && (
                <SimpleTooltip text={translate(Content.map8.comments.delete)} placement="bottom">
                    <StyledInteractiveElement data-testid="delete-comment-button" onClick={onDeleteClicked}>
                        <StyledCommentIcon icon={["far", "trash"]} size="lg" />
                    </StyledInteractiveElement>
                </SimpleTooltip>
            )}
        </StyledCommentActionWrapper>
    );
};

export const CommentComponent = ({
    textContent,
    userId,
    handleEditClick,
    handleDeleteClick,
    handleViewAll,
    handleImageUpload,
    lastUpdatedAt,
    createdAt,
    id,
    inCommentList = false,
    className,
    dataTestId,
    disableImageButton: disablePhotoButton,
}: CommentProps) => {
    const translate = useIventisTranslate();

    const { getUser, getUserProfilePicture } = useCommentsService();

    const { data: user, isLoading: isUserLoading } = useQuery(["user", userId], async () => {
        const [user, userProfilePicture] = await Promise.all([getUser(userId), getUserProfilePicture(userId)]);
        return { name: `${user.firstName} ${user.lastName}`, profileImg: userProfilePicture };
    });

    const { commentImageIds, commentText } = useMemo(() => parseCommentTextContent(textContent), [textContent]);

    return (
        <StyledCommentWrapperWithPadding key={id} className={className}>
            <StyledCommentHeader className="comment-header">
                {!isUserLoading && <StyledUser name={user.name} profileImg={user.profileImg} />}
                <CommentActions
                    handleDeleteClick={handleDeleteClick}
                    handleEditClick={handleEditClick}
                    disableImageButton={disablePhotoButton}
                    inCommentList={inCommentList}
                    handleImageUpload={handleImageUpload}
                />
            </StyledCommentHeader>
            <StyledTextContainer className="comment-body" data-testid={dataTestId ?? "comment"} isInCommentList={inCommentList}>
                {commentText}
                {commentImageIds.map((commentImageId) => (
                    <CommentImageComponent key={commentImageId} imageId={commentImageId} fullScreenIcon={!inCommentList} />
                ))}
            </StyledTextContainer>
            <StyledFooter className="comment-footer">
                <StyledEditMeta>
                    {timeAgo(createdAt, translate(Content.comments.justNow))}
                    {lastUpdatedAt != null && lastUpdatedAt !== createdAt && <StyledEmphasis data-testid="comment-edited">{translate(Content.comments.edited)}</StyledEmphasis>}
                </StyledEditMeta>
                {!inCommentList && (
                    <StyledLink data-testid="view-all-comments" onClick={handleViewAll}>
                        {translate(Content.comments.viewAllComments)}
                    </StyledLink>
                )}
            </StyledFooter>
        </StyledCommentWrapperWithPadding>
    );
};

export const StyledEditMeta = styled.p`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
`;

const StyledEmphasis = styled.em`
    margin-left: 5px;
`;

export const StyledCommentWrapperWithPadding = styled(StyledCommentWrapper)`
    padding: 15px;
    box-shadow: none;
    width: 100%;
    border: none;
    ${media.medium} {
        border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    }
`;

const StyledUser = styled(User)`
    display: flex;
    font-weight: 500;
    place-items: center;
    gap: 10px;
    padding: 10px 0;
    min-width: 0;
    p {
        max-width: 220px;
    }
`;

export const StyledTextContainer = styled.div<{ isInCommentList: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    white-space: pre-line;
    margin-bottom: 15px;
    max-height: ${(props) => (props.isInCommentList ? "auto" : "400px")};
    overflow-y: auto;
`;

export const StyledFooter = styled.footer`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    padding: 14px 0;
    > button {
        min-width: 120px;
    }
`;

const StyledUserThumbnail = styled(UserThumbnail)`
    width: 24px;
    height: 24px;
`;

export const StyledCommentHeader = styled.header`
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-bottom: 15px;
`;

const StyledInteractiveElement = styled(InteractiveElement)`
    padding: 14px;
`;

const StyledLink = styled(InteractiveElement)`
    text-decoration: underline;
    :not([disabled]) {
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    }
`;

const StyledCommentActionWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: fit-content;
`;
